// 2026-10-13 addition — UI for 3 admin actions that existed on the
// backend (built/reviewed earlier the same day) but had ZERO frontend
// caller: POST /admin/users/{sub}/reset-credentials, POST /admin/users/
// {sub}/change-email, and POST /admin/clinician-orders/{order_id}/
// resend-patient-consent. An admin could not trigger any of them without
// calling the API directly.
//
// Shared between AdminDashboard.js (patients AND clinicians both live in
// that one user table) and AdminClinicians.js (the per-practice clinician
// view) rather than duplicating ~80 lines of dialog/Alert/loading-state
// logic twice — both pages already share the exact same admin navy/gold
// chrome and MUI Dialog/Alert conventions (see NewClinicianDialog in
// AdminClinicians.js, the only existing confirm/dialog pattern in this
// app), copied here rather than inventing a new one.
//
// Real backend response shapes, confirmed by reading backend/main.py
// directly before writing this (never guessed):
//   - POST /admin/users/{sub}/reset-credentials -> {"status": "ok"}. The
//     new temp password is emailed directly to the account's address on
//     file — it is NEVER returned in the response body for display, so
//     this dialog's success state intentionally shows no password.
//   - POST /admin/users/{sub}/change-email -> body {new_email, reason}
//     (both required; `reason` 422s if blank) -> {"status": "ok"}. This
//     ALSO forces a password reset in the same write (see
//     admin_change_user_email's _new_temp_password_fields call) and
//     emails the new temp credentials to the NEW address — the old
//     address stops being able to log in at all. Same "no password in
//     the response" rule as reset-credentials above.
//   - POST /admin/clinician-orders/{order_id}/resend-patient-consent ->
//     {"status": status_} where status_ is one of "sent" / "no_email" /
//     "email_failed" / "not_eligible" — all returned with HTTP 200 (the
//     backend's _issue_patient_consent_link never raises). A 200 is NOT
//     the same as "the link actually went out" — each of the 4 values
//     gets its own distinct Alert below; only "sent" is a plain success.
import React, { useState, useEffect, useRef } from "react";
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  TextField, CircularProgress, Tooltip, IconButton, Autocomplete,
} from "@mui/material";
import { LockReset, AlternateEmail, Forward } from "@mui/icons-material";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a" };

// ── Reset credentials ──────────────────────────────────────────────────
// `onDone` — optional (2026-10-13 addition, review-pass fix): fires once
// the admin dismisses the success dialog, same deferred-until-close
// pattern ChangeEmailButton's `onChanged` uses — a reset also sets
// `force_password_change: true` server-side, which AdminClinicians.js's
// "Active / Pending first login" Status chip reads, so without a
// refresh that chip silently goes stale after a successful reset.
export function ResetCredentialsButton({ sub, email, token, iconOnly, onDone }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  // 2026-10-14 fix (confirmed real gap, High severity) — the backend used
  // to always report plain success here even when the credentials email
  // actually failed to send, so an admin had no way to know the user
  // never received their new password. These two now reflect the
  // backend's real, checked outcome.
  const [emailSent, setEmailSent] = useState(true);
  const [tempPassword, setTempPassword] = useState(null);

  const handleClose = () => {
    if (submitting) return;
    const wasDone = done;
    setOpen(false);
    setError(null);
    setDone(false);
    setEmailSent(true);
    setTempPassword(null);
    if (wasDone) onDone && onDone();
  };

  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE}/admin/users/${encodeURIComponent(sub)}/reset-credentials`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailSent(res.data?.email_sent !== false);
      setTempPassword(res.data?.temp_password || null);
      setDone(true);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not reset credentials for this user.");
    } finally {
      setSubmitting(false);
    }
  };

  const trigger = iconOnly ? (
    <Tooltip title="Reset credentials">
      <IconButton size="small" disabled={!sub} onClick={() => setOpen(true)}>
        <LockReset fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : (
    <Button size="small" startIcon={<LockReset fontSize="small" />} disabled={!sub} onClick={() => setOpen(true)}
      sx={{ textTransform: "none", fontWeight: 600 }}>
      Reset Credentials
    </Button>
  );

  return (
    <>
      {trigger}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Reset Credentials</DialogTitle>
        <DialogContent>
          {done ? (
            emailSent ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                New temporary credentials were generated and emailed to {email || "this user's address on file"}.
                They'll be required to set a new password on next login.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                New temporary credentials were generated, but the email to {email || "this user's address on file"} could
                not be sent. The password HAS already been reset — deliver this temporary password to the user yourself:
                {" "}<strong style={{ fontFamily: "monospace" }}>{tempPassword}</strong>
              </Alert>
            )
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              This will generate new temporary credentials for {email ? <strong>{email}</strong> : "this user"} — continue?
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {done ? (
            <Button onClick={handleClose} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
              <Button onClick={confirm} variant="contained" disabled={submitting} sx={{ bgcolor: BRAND.navy }}>
                {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Reset Credentials"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Change email ────────────────────────────────────────────────────────
export function ChangeEmailButton({ sub, email, token, iconOnly, onChanged }) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  // 2026-10-13 fix — real regression caught in review: calling onChanged
  // immediately on success (in the same tick as setDone(true)) let the
  // PARENT table update its row's email/key before this dialog's own
  // success Alert ever painted. Both AdminDashboard.js and
  // AdminClinicians.js key their rows by email — updating the email
  // remounts this exact component (new key = new instance = open: false),
  // so the success message was closing itself before anyone could read
  // it. `onChanged` now only fires from handleClose, once the admin has
  // actually seen the confirmation and dismissed it themselves.
  const [emailSent, setEmailSent] = useState(true);
  const [tempPassword, setTempPassword] = useState(null);
  // 2026-10-22 (Piece 2, one-identity-one-email invariant, downstream-
  // mismatch-checker finding) — this route's response now carries a
  // conditional identity_claim_synced flag (see main.py's
  // _admin_change_user_email_core); without surfacing it here, an admin
  // using THIS panel (unlike labariumaireport's UserDashboard.js, already
  // fixed) would see a plain "success" even when the identity-claim
  // auto-sync silently failed server-side.
  const [identityClaimSynced, setIdentityClaimSynced] = useState(true);

  const handleClose = () => {
    if (submitting) return;
    const changedEmail = done ? newEmail.trim() : null;
    setOpen(false);
    setNewEmail("");
    setReason("");
    setError(null);
    setDone(false);
    setEmailSent(true);
    setTempPassword(null);
    setIdentityClaimSynced(true);
    if (changedEmail) onChanged && onChanged(changedEmail);
  };

  // Matches the same inline-error convention FieldLabel/DynamicRequisitionForm.js
  // established elsewhere in this app (red helper text under the field),
  // rather than only a top-of-dialog Alert.
  const newEmailTouched = newEmail.length > 0;
  const emailFormatValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail.trim());
  const newEmailError = newEmailTouched && !emailFormatValid;
  const reasonTouched = reason.length > 0;
  const reasonError = reasonTouched && !reason.trim();
  const canSubmit = emailFormatValid && reason.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE}/admin/users/${encodeURIComponent(sub)}/change-email`, {
        new_email: newEmail.trim(), reason: reason.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEmailSent(res.data?.email_sent !== false);
      setTempPassword(res.data?.temp_password || null);
      setIdentityClaimSynced(res.data?.identity_claim_synced !== false);
      // onChanged fires from handleClose (once the admin dismisses the
      // success Alert), not here — see handleClose's own comment above.
      setDone(true);
    } catch (err) {
      // A Pydantic 422 can hand back `detail` as an array of error objects,
      // not a string — rendering that raw inside <Alert> would crash the
      // page, so only a genuine string detail is ever shown.
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not change this user's email.");
    } finally {
      setSubmitting(false);
    }
  };

  const trigger = iconOnly ? (
    <Tooltip title="Change email">
      <IconButton size="small" disabled={!sub} onClick={() => setOpen(true)}>
        <AlternateEmail fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : (
    <Button size="small" startIcon={<AlternateEmail fontSize="small" />} disabled={!sub} onClick={() => setOpen(true)}
      sx={{ textTransform: "none", fontWeight: 600 }}>
      Change Email
    </Button>
  );

  return (
    <>
      {trigger}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Change Email</DialogTitle>
        <DialogContent>
          {done ? (
            <>
              {emailSent ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                  This account's login email was changed to <strong>{newEmail.trim()}</strong>. New temporary credentials
                  were emailed to the new address — the previous address can no longer sign in.
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mt: 1 }}>
                  This account's login email was changed to <strong>{newEmail.trim()}</strong>, but the credentials email
                  to that address FAILED to send. The old address can no longer log in and the new address has no way to
                  receive the password — the user is locked out until you deliver this temporary password yourself:
                  {" "}<strong style={{ fontFamily: "monospace" }}>{tempPassword}</strong>
                </Alert>
              )}
              {/* 2026-10-22 (Piece 2, one-identity-one-email invariant,
                  downstream-mismatch-checker finding) — the account's own
                  email change always auto-syncs this patient's identity
                  claim; if that secondary write fails, it must never be
                  silent — same fix already applied to labariumaireport's
                  UserDashboard.js, mirrored here. */}
              {!identityClaimSynced && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  The login email changed successfully, but this patient's identity record could not be
                  automatically updated to match. Use the Identity Linking dashboard to correct it by hand.
                </Alert>
              )}
            </>
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Alert severity="info" sx={{ mb: 2 }}>
                This also resets the account's password — new temporary credentials will be emailed to the new
                address, and the current address will no longer be able to log in.
              </Alert>
              <TextField
                label="New email" type="email" fullWidth sx={{ mb: 2, mt: 0.5 }}
                value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                error={newEmailError}
                helperText={newEmailError ? "Enter a valid email address." : (email ? `Current: ${email}` : " ")}
              />
              <TextField
                label="Reason for change" required fullWidth multiline minRows={2}
                value={reason} onChange={(e) => setReason(e.target.value)}
                error={reasonError}
                helperText={reasonError ? "A reason is required for the audit trail." : "Required — recorded in the audit log."}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {done ? (
            <Button onClick={handleClose} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} variant="contained" disabled={!canSubmit || submitting} sx={{ bgcolor: BRAND.navy }}>
                {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Change Email"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Resend patient consent (admin-triggered) ────────────────────────────
// 2026-10-14 fix — real gap, reported directly with evidence: this used to
// require typing a raw order ID blind, with no lookup anywhere and no way
// to confirm who a link was about to be resent to. GET /admin/orders/search
// (new) searches this app's OWN clinician_orders/clinician_patient_records
// natively — no cross-service call needed, this app owns the data.
export function ResendPatientConsentTool({ token }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // "sent" | "no_email" | "email_failed" | "not_eligible"
  const searchSeq = useRef(0);

  useEffect(() => {
    if (!query.trim()) { setOptions([]); return; }
    const seq = ++searchSeq.current;
    setSearching(true);
    const handle = setTimeout(() => {
      axios.get(`${API_BASE}/admin/orders/search`, {
        params: { patient_name: query.trim() },
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (searchSeq.current !== seq) return; // superseded by a later keystroke
          setOptions(res.data?.orders || []);
        })
        .catch(() => { if (searchSeq.current === seq) setOptions([]); })
        .finally(() => { if (searchSeq.current === seq) setSearching(false); });
    }, 300); // debounce — don't fire a search on every keystroke
    return () => clearTimeout(handle);
  }, [query, token]);

  const orderId = selectedOrder?.order_id || "";
  const reset = () => { setQuery(""); setOptions([]); setSelectedOrder(null); setConfirming(false); setError(null); setResult(null); };
  const handleClose = () => { if (submitting) return; setOpen(false); reset(); };

  const doResend = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_BASE}/admin/clinician-orders/${encodeURIComponent(orderId.trim())}/resend-patient-consent`,
        {}, { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(res.data?.status || null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not resend the consent link for this order.");
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  // Each of the 4 real backend status values gets its own distinct
  // message — a bare 200 is NOT the same as "the link went out" (see this
  // file's header comment), so this never collapses all 4 into one
  // generic "success" Alert.
  const resultAlert = () => {
    if (!result) return null;
    if (result === "sent") {
      return <Alert severity="success">A fresh consent link was sent to the patient.</Alert>;
    }
    if (result === "no_email") {
      return <Alert severity="warning">No email or phone number on file for this patient — the link could not be sent through any channel. Add contact info to the patient's record first.</Alert>;
    }
    if (result === "email_failed") {
      return <Alert severity="warning">Every contact channel on file was attempted, but the send failed. Please try again shortly, or verify the patient's contact info.</Alert>;
    }
    if (result === "not_eligible") {
      return <Alert severity="warning">This is a legacy order from before async patient consent existed — it cannot receive a signing link.</Alert>;
    }
    return <Alert severity="info">Unrecognized response: {String(result)}</Alert>;
  };

  return (
    <>
      <Button size="small" startIcon={<Forward fontSize="small" />} onClick={() => setOpen(true)}
        variant="outlined" sx={{ textTransform: "none", fontWeight: 700, borderColor: BRAND.navy, color: BRAND.navy }}>
        Resend Patient Consent Link
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Resend Patient Consent Link</DialogTitle>
        <DialogContent>
          {result ? (
            resultAlert()
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Autocomplete
                fullWidth autoFocus disabled={confirming}
                options={options}
                loading={searching}
                value={selectedOrder}
                onChange={(e, newValue) => { setSelectedOrder(newValue); setConfirming(false); }}
                inputValue={query}
                onInputChange={(e, newInputValue) => setQuery(newInputValue)}
                getOptionLabel={(o) => {
                  if (!o) return "";
                  // 2026-10-18 fix (real gap, confirmed via an admin-
                  // visibility audit) — this tool's whole point is
                  // deciding whether to resend a consent link, but it
                  // never showed the order's own status or whether a
                  // consent link had already been sent/succeeded — an
                  // admin had to guess before resending. The backend
                  // route already returns both fields now; show them.
                  const statusPart = o.status ? ` [${o.status}]` : "";
                  const consentPart = o.patient_consent_status ? ` · consent: ${o.patient_consent_status}` : "";
                  // 2026-10-18 fix (admin field-visibility sweep) — the
                  // backend now returns when consent was given and whether
                  // biobank consent (a separate, non-defaulted checkbox)
                  // was given; show both so admin doesn't have to guess.
                  const consentDatePart = o.patient_consent_given_at ? ` on ${o.patient_consent_given_at.slice(0, 10)}` : "";
                  const biobankPart = o.biobank_consent === true ? " · biobank: yes"
                    : o.biobank_consent === false ? " · biobank: no" : "";
                  return `${o.patient_first_name} ${o.patient_last_name} (DOB ${o.patient_dob || "unknown"}) — Order ${o.order_id}${statusPart}${consentPart}${consentDatePart}${biobankPart}`;
                }}
                isOptionEqualToValue={(a, b) => a.order_id === b.order_id}
                noOptionsText={query.trim() ? "No matching orders" : "Type a patient name to search"}
                renderInput={(params) => (
                  <TextField {...params} label="Search by patient name"
                    helperText="Pick the real order — you'll see the patient's name and DOB before sending."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (<>{searching ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                    }}
                  />
                )}
                sx={{ mb: confirming ? 2 : 0 }}
              />
              {confirming && selectedOrder && (
                <Alert severity="warning">
                  This will send a fresh signing link to <strong>{selectedOrder.patient_first_name} {selectedOrder.patient_last_name}</strong> (DOB {selectedOrder.patient_dob || "unknown"}) on
                  order <strong>{selectedOrder.order_id}</strong>, invalidating any previously sent link — continue?
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {result ? (
            <Button onClick={handleClose} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
          ) : confirming ? (
            <>
              <Button onClick={() => setConfirming(false)} disabled={submitting}>Back</Button>
              <Button onClick={doResend} variant="contained" disabled={submitting} sx={{ bgcolor: BRAND.navy }}>
                {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirm Resend"}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={() => setConfirming(true)} variant="contained" disabled={!orderId.trim()} sx={{ bgcolor: BRAND.navy }}>
                Continue
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
